import { PropertyFilterQuery } from '@cloudscape-design/collection-hooks';
import { DateRangePickerProps } from '@cloudscape-design/components';

import { getApiClient, publicClient } from '../../api/client';
import type { components, operations } from '../../api/schema';
import { buildListQueryParams, withHiddenFilter } from './query';
import type { SendPublicReportRequest, SendSelectedReportsRequest } from '../types';
import { ApiNetworkError, ApiResponseError, ExpectedApiError } from './errors';

type CreateReportRequest = components['schemas']['CreateReportRequest'];
type CreateReportPublicRequest = components['schemas']['CreateReportPublicRequest'];
type CreatePublicLinkReportRequest = components['schemas']['CreatePublicLinkReportRequest'];
type AdamPublicLinkQuery = NonNullable<
  operations['validate_adam_public_scorecard_link_reports_public_link_adam_get']['parameters']['query']
>;
type CreateReportsRequest = {
  place_ids: string[];
  contact?: components['schemas']['ReportContactRequest'];
  business_type?: string;
};

function getErrorDetail(error: unknown): string | undefined {
  return typeof error === 'object' &&
    error !== null &&
    'detail' in error &&
    typeof error.detail === 'string'
    ? error.detail
    : undefined;
}

export async function createReport(body: CreateReportRequest) {
  const client = getApiClient();
  const { data, error } = await client.POST('/reports', { body });
  if (error) {
    const detail = getErrorDetail(error);
    if (detail === 'Invalid phone number: cannot send text messages') {
      throw new ExpectedApiError(detail);
    }
    throw new Error(`Failed to create report: ${JSON.stringify(error)}`);
  }
  return data;
}

export async function createReports(req: CreateReportsRequest) {
  const data: Awaited<ReturnType<typeof createReport>>[] = [];
  for (const place_id of req.place_ids) {
    const response = await createReport({
      place_id,
      contact: req.contact,
      business_type: req.business_type,
    });
    data.push(response);
  }
  return data;
}

export async function getReport(uuid: string, customerVolume?: number) {
  // This endpoint is public so shared scorecard links do not require a session.
  const endpoint = '/reports/{uuid}';
  let result: Awaited<ReturnType<typeof publicClient.GET>>;
  try {
    result = await publicClient.GET(endpoint, {
      params: {
        path: { uuid },
        query: customerVolume === undefined ? {} : { customer_volume: customerVolume },
      },
    });
  } catch {
    throw new ApiNetworkError('GET', endpoint);
  }

  const { data, error, response } = result;
  if (error && response.status === 404) throw new ExpectedApiError('Report not found');
  if (error) throw new ApiResponseError('GET', endpoint, response.status, error);
  return data;
}

export async function createReportPublic(body: CreateReportPublicRequest) {
  // Public endpoint - no auth needed
  const { data, error } = await publicClient.POST('/reports/public', { body });
  if (error) {
    const detail = getErrorDetail(error);
    if (detail === 'Invalid phone number: cannot send text messages') {
      throw new ExpectedApiError(detail);
    }
    throw new Error(`Failed to create public report: ${JSON.stringify(error)}`);
  }
  return data;
}

export async function validateAdamPublicScorecardLink(code: string) {
  const query = { code } satisfies AdamPublicLinkQuery;
  const { data, error } = await publicClient.GET('/reports/public-link/adam', {
    params: { query },
  });
  if (error || !data?.valid) {
    throw new ExpectedApiError('This scorecard link is invalid or unavailable.');
  }
  return data;
}

export async function createAdamPublicScorecard(code: string, body: CreatePublicLinkReportRequest) {
  type Query = NonNullable<
    operations['create_adam_public_scorecard_reports_public_link_adam_post']['parameters']['query']
  >;
  const query = { code } satisfies Query;
  const { data, error } = await publicClient.POST('/reports/public-link/adam', {
    params: { query },
    body,
  });
  if (error || !data) {
    throw new ExpectedApiError('Unable to create this scorecard. Please try again.');
  }
  return data;
}

export async function getReportPublic(public_code: string, customerVolume?: number) {
  // Public endpoint - no auth needed
  const { data, error, response } = await publicClient.GET('/reports/public/{public_code}', {
    params: {
      path: { public_code },
      query: customerVolume === undefined ? {} : { customer_volume: customerVolume },
    },
  });
  if (error && response.status === 404) {
    throw new ExpectedApiError('This scorecard link is invalid or unavailable.');
  }
  if (error) throw new Error(`Failed to fetch public report: ${JSON.stringify(error)}`);
  return data;
}

export async function downloadReportPdf(
  uuid: string,
  source: string,
  period: string,
  customerVolume?: number,
) {
  // Public endpoint - no auth needed. Used from the public scorecard page.
  const { data, error } = await publicClient.GET('/reports/{uuid}/{source}/{period_id}/pdf', {
    params: {
      path: { uuid, source, period_id: period },
      query: customerVolume === undefined ? {} : { customer_volume: customerVolume },
    },
    parseAs: 'blob',
  });
  if (error) throw new Error(`Failed to download report PDF: ${JSON.stringify(error)}`);
  return URL.createObjectURL(data as Blob);
}

// --- Admin/Dashboard functions ---

export async function getReports(
  query: PropertyFilterQuery,
  page = 1,
  limit = 20,
  range?: DateRangePickerProps.Value | null,
  sortingField?: string,
  sortDirection?: 'asc' | 'desc',
  now = new Date(),
  textQuery?: string,
) {
  const client = getApiClient();
  const params = buildListQueryParams({
    query,
    page, limit, range, now,
    rangeName: 'report_at',
    sortingField: sortingField ?? 'report_at',
    sortDirection: sortDirection ?? 'desc',
    textQuery,
  });
  const { data, error } = await client.GET('/reports', {
    params: { query: params },
  });
  if (error) throw new Error(`Failed to fetch reports: ${JSON.stringify(error)}`);
  return data;
}

export async function getReportsMeta() {
  const client = getApiClient();
  const { data, error } = await client.GET('/reports/meta');
  if (error) throw new Error(`Failed to fetch reports meta: ${JSON.stringify(error)}`);
  return data;
}

export async function hideReport(uuid: string) {
  const client = getApiClient();
  const { error } = await client.POST('/reports/{uuid}/hide', { params: { path: { uuid } } });
  if (error) throw new Error(`Failed to hide report: ${JSON.stringify(error)}`);
}

export async function unhideReport(uuid: string) {
  const client = getApiClient();
  const { error } = await client.POST('/reports/{uuid}/unhide', { params: { path: { uuid } } });
  if (error) throw new Error(`Failed to unhide report: ${JSON.stringify(error)}`);
}

export async function hideReports(uuids: string[]) {
  for (const uuid of uuids) await hideReport(uuid);
}

export async function unhideReports(uuids: string[]) {
  for (const uuid of uuids) await unhideReport(uuid);
}

export async function sendSelectedReports(body: SendSelectedReportsRequest) {
  const client = getApiClient();
  const { data, error } = await client.POST('/reports/send-selected', { body });
  if (error) throw new Error(`Failed to send selected reports: ${JSON.stringify(error)}`);
  if (!data) throw new Error('Failed to send selected reports: empty response');
  return data;
}

export async function sendPublicReport(uuid: string, body: SendPublicReportRequest) {
  const { data, error, response } = await publicClient.POST('/reports/{uuid}/send', {
    params: { path: { uuid } },
    body,
  });
  if (error) {
    const message =
      getErrorDetail(error) ?? 'Unable to send this scorecard right now. Please try again later.';
    if (response.status >= 400 && response.status < 500) {
      throw new ExpectedApiError(message);
    }
    throw new Error(message);
  }
  if (!data) throw new Error('Unable to send this scorecard right now. Please try again later.');
  return data;
}

export async function getReportContacts(
  query: PropertyFilterQuery,
  page = 1,
  limit = 20,
  range?: DateRangePickerProps.Value | null,
  sortingField?: string,
  sortDirection?: 'asc' | 'desc',
  now = new Date(),
) {
  const client = getApiClient();
  const params = buildListQueryParams({
    query: withHiddenFilter(query),
    page, limit, range, now,
    rangeName: 'created_at',
    sortingField: sortingField ?? 'created_at',
    sortDirection: sortDirection ?? 'desc',
  });
  const { data, error } = await client.GET('/reports/contacts', {
    params: { query: params },
  });
  if (error) throw new Error(`Failed to fetch report contacts: ${JSON.stringify(error)}`);
  return data;
}

export async function getReportContactsMeta() {
  const client = getApiClient();
  const { data, error } = await client.GET('/reports/contacts/meta');
  if (error) throw new Error(`Failed to fetch report contacts meta: ${JSON.stringify(error)}`);
  return data;
}

export async function getReportContact(uuid: string) {
  const client = getApiClient();
  const { data, error } = await client.GET('/reports/contacts/{uuid}', {
    params: { path: { uuid } },
  });
  if (error) throw new Error(`Failed to fetch report contact: ${JSON.stringify(error)}`);
  return data;
}

export async function hideReportContact(uuid: string) {
  const client = getApiClient();
  const { error } = await client.POST('/reports/contacts/{uuid}/hide', { params: { path: { uuid } } });
  if (error) throw new Error(`Failed to hide report contact: ${JSON.stringify(error)}`);
}

export async function unhideReportContact(uuid: string) {
  const client = getApiClient();
  const { error } = await client.POST('/reports/contacts/{uuid}/unhide', { params: { path: { uuid } } });
  if (error) throw new Error(`Failed to unhide report contact: ${JSON.stringify(error)}`);
}

export async function hideReportContacts(uuids: string[]) {
  for (const uuid of uuids) await hideReportContact(uuid);
}

export async function unhideReportContacts(uuids: string[]) {
  for (const uuid of uuids) await unhideReportContact(uuid);
}
